/**
 * Slack OAuth state handling for CSRF protection.
 * Store random state -> workspace_id mapping before redirect; validate on callback.
 */
const STORAGE_KEY = 'pr_slack_oauth_state';
const TTL_MS = 10 * 60 * 1000; // 10 minutes

interface StoredState {
  state: string;
  workspace_id: string;
  ts: number;
}

/** Generate random state and store workspace_id mapping. Returns state for OAuth URL. */
export function storeSlackOAuthState(workspaceId: string): string {
  const state = crypto.randomUUID();
  const data: StoredState = { state, workspace_id: workspaceId, ts: Date.now() };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Fallback if localStorage unavailable
  }
  return state;
}

/** Look up workspace_id from stored state. Returns null if invalid or expired. */
export function getWorkspaceIdFromSlackState(state: string): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data: StoredState = JSON.parse(raw);
    if (data.state !== state) return null;
    if (Date.now() - data.ts > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    localStorage.removeItem(STORAGE_KEY);
    return data.workspace_id;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}
